/**
 * Organizations Custom Hooks
 *
 * Business logic hooks for organization management features.
 */

import { useCallback, useMemo, useState } from 'react';
import { useUser } from '@features/user';
import { useStatsigClient } from '@statsig/react-bindings';
import type {
  ProviderInvitationResponse,
  ProviderOrganizationMemberResponse,
} from '@store/generated/organizations';
import { hasBillingAccess } from '@utils/roleUtils';

import type {
  Clinician,
  LoadingOperations,
  OrganizationInvitation,
  OrganizationMemberTableRow,
  PermissionsMatrixConfig,
} from '../types';
import {
  DEFAULT_PERMISSIONS_MATRIX_CONFIG,
} from '../types';

import { useGetOrganizationMembersQuery, useGetOrganizationQuery } from './api';

/**
 * Transform API member data to table row format
 */
export function useTransformMembers(
  members?: ProviderOrganizationMemberResponse[]
): {
  activeMembers: OrganizationMemberTableRow[];
  inactiveMembers: OrganizationMemberTableRow[];
} {
  return useMemo(() => {
    if (!members) {
      return { activeMembers: [], inactiveMembers: [] };
    }

    const transformed = members.map(
      (member): OrganizationMemberTableRow => ({
        userId: member.user_id,
        name: member.name || 'Unknown',
        email: member.email || '',
        roles: member.roles || [],
        status: member.status === 'inactive' ? 'inactive' : 'active',
        deactivatedAt: member.deactivated_at,
        notesCreated: 0, // This would need to come from additional user metrics
        phoneNumber: member.mobile_phone_num,
        profilePicture: undefined, // Not available in provider response
        bundles: member.bundles?.map(b => ({
          assignment_id: b.assignment_id,
          bundle_id: b.bundle_id,
          bundle_name: b.bundle_name || 'Unknown',
          bundle_type: (b.bundle_type || 'add_on') as 'tier' | 'add_on',
          status: b.status,
        })) || [],
        billingInfo: member.billing_info || null,
        firstName: undefined,
        lastName: undefined,
      })
    );

    const activeMembers = transformed.filter(m => m.status === 'active');
    const inactiveMembers = transformed.filter(m => m.status === 'inactive');

    return { activeMembers, inactiveMembers };
  }, [members]);
}

/**
 * Transform API invitation data
 */
export function useTransformInvitations(
  invitations?: ProviderInvitationResponse[]
): OrganizationInvitation[] {
  return useMemo(() => {
    if (!invitations) return [];

    return invitations.map((invitation): OrganizationInvitation => ({
      invitationId: invitation.invitation_id,
      email: invitation.email,
      role: invitation.role,
      sentAt: invitation.sent_at,
      expiresAt: invitation.expires_at,
      status: invitation.status,
      bundleIds: invitation.bundle_ids,
    }));
  }, [invitations]);
}

/**
 * Hook for managing loading states for individual operations
 */
export function useOperationLoading() {
  const [loadingOperations, setLoadingOperations] = useState<LoadingOperations>({});

  const setOperationLoading = useCallback((key: string, loading: boolean) => {
    setLoadingOperations(prev => ({
      ...prev,
      [key]: loading,
    }));
  }, []);

  const isOperationLoading = useCallback(
    (key: string) => loadingOperations[key] || false,
    [loadingOperations]
  );

  return {
    loadingOperations,
    setOperationLoading,
    isOperationLoading,
  };
}

/**
 * Check if an invitation is expired
 */
export function useInvitationStatus(expiresAt: string): 'pending' | 'expired' {
  return useMemo(() => {
    const expiryDate = new Date(expiresAt);
    return Date.now() > expiryDate.getTime() ? 'expired' : 'pending';
  }, [expiresAt]);
}

/**
 * Hook for organization access and permissions
 *
 * Provides common organization data and permission checks used across
 * organization management pages.
 */
export function useOrganizationAccess() {
  const { userState } = useUser();

  const userIsBillingAdmin = hasBillingAccess(userState?.group_user_roles);
  const userIsInGroup = (userState?.group_ids ?? []).length > 0;

  const {
    data: organizationData,
    isLoading: orgLoading,
    refetch: refetchOrg,
  } = useGetOrganizationQuery(undefined, {
    skip: !userIsInGroup,
  });

  return {
    userState,
    userIsBillingAdmin,
    userIsInGroup,
    organizationData,
    orgLoading,
    refetchOrg,
  };
}

// ============================================================================
// Permissions Matrix Config Hook
// ============================================================================

const PERMISSIONS_MATRIX_CONFIG_NAME = 'permissions_matrix_config';

/**
 * Hook to get Permissions Matrix configuration from Statsig dynamic config.
 *
 * This allows the permissions matrix content (title, roles, permissions) to be
 * configured remotely without code changes.
 *
 * @returns PermissionsMatrixConfig with dynamic values or defaults
 *
 * @example
 * const config = usePermissionsMatrixConfig();
 * // Use config.title, config.roles, config.permissions, etc.
 */
export function usePermissionsMatrixConfig(): PermissionsMatrixConfig {
  const { client } = useStatsigClient();

  return useMemo(() => {
    const dynamicConfig = client.getDynamicConfig(PERMISSIONS_MATRIX_CONFIG_NAME);
    const configValue = dynamicConfig.value as Record<string, unknown> | null;

    // Safely extract values with type guards
    const title =
      configValue && typeof configValue.title === 'string'
        ? configValue.title
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.title;

    const subtitle =
      configValue && typeof configValue.subtitle === 'string'
        ? configValue.subtitle
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.subtitle;

    const notes =
      configValue && Array.isArray(configValue.notes)
        ? (configValue.notes as string[])
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.notes;

    const legend =
      configValue &&
      configValue.legend &&
      typeof configValue.legend === 'object' &&
      !Array.isArray(configValue.legend) &&
      typeof (configValue.legend as Record<string, unknown>).default === 'string' &&
      typeof (configValue.legend as Record<string, unknown>).configurable === 'string' &&
      typeof (configValue.legend as Record<string, unknown>).coming_soon === 'string'
        ? (configValue.legend as PermissionsMatrixConfig['legend'])
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.legend;

    const roles =
      configValue && Array.isArray(configValue.roles)
        ? (configValue.roles as PermissionsMatrixConfig['roles'])
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.roles;

    const permissions =
      configValue && Array.isArray(configValue.permissions)
        ? (configValue.permissions as PermissionsMatrixConfig['permissions'])
        : DEFAULT_PERMISSIONS_MATRIX_CONFIG.permissions;

    return {
      title,
      subtitle,
      notes,
      legend,
      roles,
      permissions,
    };
  }, [client]);
}

// ============================================================================
// Clinicians Hook
// ============================================================================

/**
 * Fetch clinician members for the current organization.
 *
 * Thin wrapper around useGetOrganizationMembersQuery that filters
 * by role='clinician' and returns a simplified list suitable for
 * dropdowns, multi-selects, and filter bars.
 *
 * @param options.skip - Skip the query (e.g. when user lacks org access)
 */
export function useClinicians(options?: { skip?: boolean }) {
  const { data, isLoading, isFetching, error, refetch } =
    useGetOrganizationMembersQuery(
      { role: 'clinician' },
      { skip: options?.skip },
    );

  const clinicians: Clinician[] = useMemo(() => {
    if (!data?.members) return [];
    return data.members
      .filter((m) => m.status !== 'inactive')
      .map((m) => ({ userId: m.user_id, name: m.name || 'Unknown' }));
  }, [data?.members]);

  return { clinicians, isLoading, isFetching, error, refetch };
}